import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import AssignmentIcon from '@mui/icons-material/Assignment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AppLayout from '../components/AppLayout';
import { formatReviewStatus, getReviewStatusColor, getReviewStatusFilterKey, REVIEW_STATUS_FILTER_OPTIONS } from '../utils/reviewStatus';
import { formatDateRange, utcMs } from '../utils/dateFormat';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad } from '../utils/userFacingCopy';

interface Review {
  id: string;
  reviewCycleId: string;
  reviewCycleName?: string;
  employeeId: string;
  employeeName?: string;
  managerId?: string;
  status: string | number;
  excludeRequestedAt?: string;
}

interface ReviewCycle {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
}

export default function ReviewsToManagePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const cycleId = searchParams.get('cycleId') ?? '';
  const { user } = useAuth();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewCycles, setReviewCycles] = useState<ReviewCycle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<'employeeName' | 'reviewCycleName' | 'status'>('reviewCycleName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Load active review cycles for the dropdown
  useEffect(() => {
    apiService.get<ReviewCycle[]>('/review-cycles')
      .then((res) => {
        const active = (res.data ?? [])
          .filter((c) => c.isActive)
          .sort((a, b) => utcMs(a.startDate) - utcMs(b.startDate));
        setReviewCycles(active);
      })
      .catch(() => setReviewCycles([]));
  }, []);

  const handleCycleChange = (newCycleId: string) => {
    const next = new URLSearchParams(searchParams);
    if (newCycleId) {
      next.set('cycleId', newCycleId);
    } else {
      next.delete('cycleId');
    }
    setSearchParams(next, { replace: true });
    setPage(0);
  };

  const loadReviews = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      setError('');
      const url = cycleId ? `/reviews/managed?cycleId=${encodeURIComponent(cycleId)}` : '/reviews/managed';
      const response = await apiService.get<Review[]>(url);
      const directReports = response.data.filter((r) => r.managerId === user.id);
      setReviews(directReports);
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotLoad('reviews to manage')));
    } finally {
      setLoading(false);
    }
  }, [user?.id, cycleId]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const filteredReviews = useMemo(() => {
    let filtered = [...reviews];
    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        (r) =>
          r.employeeName?.toLowerCase().includes(query) ||
          r.reviewCycleName?.toLowerCase().includes(query)
      );
    }
    if (statusFilter === 'pending') {
      filtered = filtered.filter((r) => {
        const key = getReviewStatusFilterKey(r.status);
        return key !== 'finalised' && key !== 'underdispute';
      });
    } else if (statusFilter !== 'all') {
      filtered = filtered.filter((r) => getReviewStatusFilterKey(r.status) === statusFilter.toLowerCase());
    }
    return filtered;
  }, [reviews, searchTerm, statusFilter]);

  const sortedReviews = useMemo(() => {
    return [...filteredReviews].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'employeeName') {
        cmp = (a.employeeName ?? '').localeCompare(b.employeeName ?? '');
      } else if (sortField === 'reviewCycleName') {
        cmp = (a.reviewCycleName ?? '').localeCompare(b.reviewCycleName ?? '');
      } else {
        cmp = formatReviewStatus(a.status).localeCompare(formatReviewStatus(b.status));
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [filteredReviews, sortField, sortDirection]);

  const paginatedReviews = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedReviews.slice(start, start + rowsPerPage);
  }, [sortedReviews, page, rowsPerPage]);

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setPage(0);
  };

  const getSortIcon = (field: typeof sortField) => {
    if (sortField !== field) return <UnfoldMoreIcon sx={{ fontSize: 16, opacity: 0.5 }} />;
    return sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto', pb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate(-1)} sx={{ ml: -1 }} title="Back">
              <ArrowBackIcon />
            </IconButton>
            <VisibilityIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
            <Typography variant="h4" component="h1" fontWeight="bold">
              Reviews to Manage
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {filteredReviews.length} review{filteredReviews.length !== 1 ? 's' : ''} to manage
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          Reviews for your direct reports. Use search and filters to find specific reviews. Click a row to open the review.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                placeholder="Employee name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: 1, minWidth: 250 }}
              />
              {reviewCycles.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 260 }}>
                  <InputLabel shrink id="to-manage-cycle-filter-label">Review cycle</InputLabel>
                  <Select
                    labelId="to-manage-cycle-filter-label"
                    value={cycleId}
                    label="Review cycle"
                    onChange={(e) => handleCycleChange(e.target.value)}
                    displayEmpty
                    notched
                    renderValue={(value) => {
                      if (value === '') return `All active cycles (${reviewCycles.length})`;
                      const cycle = reviewCycles.find((c) => c.id === value);
                      if (!cycle) return value;
                      return cycle.startDate && cycle.endDate
                        ? `${cycle.name} (${formatDateRange(cycle.startDate, cycle.endDate)})`
                        : cycle.name;
                    }}
                  >
                    <MenuItem value="">All active cycles ({reviewCycles.length})</MenuItem>
                    {reviewCycles.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                        {c.startDate && c.endDate && ` (${formatDateRange(c.startDate, c.endDate)})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="pending">Pending only</MenuItem>
                  <MenuItem value="all">All statuses</MenuItem>
                  {REVIEW_STATUS_FILTER_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {(searchTerm.trim() !== '' || statusFilter !== 'all') && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                {searchTerm.trim() !== '' && (
                  <Chip
                    label={`Search: "${searchTerm}"`}
                    onDelete={() => setSearchTerm('')}
                    size="small"
                    color="primary"
                  />
                )}
                {statusFilter !== 'all' && (() => {
                  const label = statusFilter === 'pending'
                    ? 'Pending only'
                    : (REVIEW_STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter)?.label ?? formatReviewStatus(statusFilter));
                  return (
                    <Chip
                      label={`Status: ${label}`}
                      onDelete={() => setStatusFilter('all')}
                      size="small"
                      color={statusFilter === 'pending' ? 'info' : getReviewStatusColor(statusFilter)}
                    />
                  );
                })()}
              </Box>
            )}
          </CardContent>
        </Card>

        <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <TableContainer sx={{ borderTop: 0 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('employeeName')}>
                        <strong>Employee</strong>
                        {getSortIcon('employeeName')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('reviewCycleName')}>
                        <strong>Cycle</strong>
                        {getSortIcon('reviewCycleName')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleSort('status')}>
                        <strong>Status</strong>
                        {getSortIcon('status')}
                      </Box>
                    </TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedReviews.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4, border: 'none' }}>
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                          <AssignmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            {searchTerm || statusFilter !== 'all' ? 'No reviews match your search or filters.' : 'No pending reviews to manage.'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {searchTerm || statusFilter !== 'all'
                              ? 'Try adjusting your search or filter criteria.'
                              : 'All reviews for your direct reports are up to date'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedReviews.map((review) => (
                      <TableRow
                        key={review.id}
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                        onClick={() => navigate(`/reviews/${review.id}`)}
                      >
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {review.employeeName || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>{review.reviewCycleName || '—'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Chip label={formatReviewStatus(review.status)} size="small" color={getReviewStatusColor(review.status)} />
                            {review.excludeRequestedAt && String(review.status).toLowerCase() !== 'excluded' && (
                              <Chip label="Exclude Requested" size="small" color="warning" variant="outlined" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/reviews/${review.id}`)}
                            color="primary"
                            title="View review"
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={sortedReviews.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}
            />
          </CardContent>
        </Card>
      </Box>
    </AppLayout>
  );
}
